// Hyperliquid public info API: candles for the chart and 24h stats for the market bar. Display only;
// trades settle at the HyperCore book mid read onchain by the oracle.
import { config, MARKETS } from '../config.ts';

export type Candle = { t: number; o: number; h: number; l: number; c: number };
export type AssetCtx = { dayNtlVlm: number; prevDayPx: number; markPx: number };

/** `tick` draws every price update as a line; the others are candles */
export type Timeframe = 'tick' | '1s' | '5s' | '15s' | '1m' | '5m' | '15m' | '1h' | '4h' | '1d';
export type Interval = Exclude<Timeframe, 'tick'>;
export const INTERVAL_MS: Record<Interval, number> = {
  '1s': 1_000,
  '5s': 5_000,
  '15s': 15_000,
  '1m': 60_000,
  '5m': 300_000,
  '15m': 900_000,
  '1h': 3_600_000,
  '4h': 14_400_000,
  '1d': 86_400_000,
};

/** Only mainnet prices match the HyperCore book the contracts read */
export const hlEnabled = config.chainId === 999;

/** Candles Hyperliquid serves; seconds candles (and every candle on a local chain) are built from ticks */
export const HL_INTERVALS: Interval[] = ['1m', '5m', '15m', '1h', '4h', '1d'];
export const TIMEFRAMES: Timeframe[] = ['tick', '1s', '5s', '15s', '1m', '5m', '15m', '1h', '4h', '1d'];
export const DEFAULT_TIMEFRAME: Timeframe = 'tick';

async function info<T>(body: unknown): Promise<T> {
  const res = await fetch(config.hlInfoUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`hyperliquid info HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

export async function candles(coin: string, interval: Interval, count = 240): Promise<Candle[]> {
  const endTime = Date.now();
  const raw = await info<{ t: number; o: string; h: string; l: string; c: string }[]>({
    type: 'candleSnapshot',
    req: { coin, interval, startTime: endTime - INTERVAL_MS[interval] * count, endTime },
  });
  return raw.map((k) => ({ t: k.t, o: +k.o, h: +k.h, l: +k.l, c: +k.c }));
}

type RawCtx = { dayNtlVlm: string; prevDayPx: string; markPx: string };
const ctx = (c: RawCtx): AssetCtx => ({ dayNtlVlm: +c.dayNtlVlm, prevDayPx: +c.prevDayPx, markPx: +c.markPx });

/**
 * 24h stats of the listed markets, by Hyperliquid coin name: validator-operated perps (`HYPE`), HIP-3 perps
 * (`xyz:SP500`, one request per dex) and spot pairs (`@107`); only the kinds listed are requested
 */
export async function assetCtxs(): Promise<Record<string, AssetCtx>> {
  const coins = Object.values(MARKETS).map((m) => m.hlCoin);
  const dexes = [...new Set(coins.filter((c) => !c.startsWith('@')).map((c) => (c.includes(':') ? c.split(':')[0]! : '')))];
  const perps = dexes.map((dex) =>
    info<[{ universe: { name: string }[] }, RawCtx[]]>({ type: 'metaAndAssetCtxs', ...(dex ? { dex } : {}) }).then(([meta, ctxs]) =>
      meta.universe.flatMap((u, i) => (ctxs[i] ? [[u.name, ctx(ctxs[i]!)] as const] : [])),
    ),
  );
  // spot contexts carry their pair's name (`@107`) themselves
  const spot = coins.some((c) => c.startsWith('@'))
    ? [info<[unknown, (RawCtx & { coin: string })[]]>({ type: 'spotMetaAndAssetCtxs' }).then(([, ctxs]) => ctxs.map((c) => [c.coin, ctx(c)] as const))]
    : [];
  return Object.fromEntries((await Promise.all([...perps, ...spot])).flat());
}
